"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  SetStateAction,
  Dispatch
} from "react";
import posthog from "posthog-js";
import { TheorieResponse } from "@/services/api_types";
import { useAppContext } from "./AppContext";
import { useLibraryContext } from "./LibraryContext";
import { DEMO_THEORIEEN } from "@/containers/framerlanding/demo/demoTheorieen";
import { useFeatureDiscovery } from "@/hooks/useFeatureDiscovery";
import { isFeatureDiscoveryTourSuppressed } from "@/lib/featureDiscoveryTourGuard";

interface TheoryContextType {
  showTheory: boolean;
  setShowTheory: Dispatch<SetStateAction<boolean>>;

  isLoadingTheory: boolean;
  setIsLoadingTheory: Dispatch<SetStateAction<boolean>>;

  theoryMenuWidth: number;
  setTheoryMenuWidth: Dispatch<SetStateAction<number>>;

  currentTheorie: TheorieResponse | undefined;
  closeTheory: (restoreSidebar?: boolean) => void;
  handleTheorieClick: (id: number) => void;

  /** Inline theorie shown directly under the active step (demo tour variant). */
  inlineTheorie: TheorieResponse | undefined;
  isLoadingInlineTheorie: boolean;
  handleInlineTheorieClick: (id: number) => void;
  closeInlineTheorie: () => void;
}

const TheoryContext = createContext<TheoryContextType | undefined>(undefined);

export function TheoryContextProvider({ children }: { children: ReactNode }) {
  const { api } = useAppContext();
  const { setSideMenuExpanded, currentStappenplan } = useLibraryContext();
  const { markDiscovered } = useFeatureDiscovery();

  const [showTheory, setShowTheory] = useState(false);
  const [isLoadingTheory, setIsLoadingTheory] = useState(false);
  const [theoryMenuWidth, setTheoryMenuWidth] = useState(400);
  const [currentTheorie, setCurrentTheorie] = useState<TheorieResponse>();

  const [inlineTheorie, setInlineTheorie] = useState<TheorieResponse>();
  const [isLoadingInlineTheorie, setIsLoadingInlineTheorie] = useState(false);

  const closeTheory = useCallback((restoreSidebar = true) => {
    if (currentTheorie) {
      posthog.capture("theorie_closed", {
        theorie_id: currentTheorie.id,
        theorie_titel: currentTheorie.titel
      });
    }
    setCurrentTheorie(undefined);
    setShowTheory(false);
    if (restoreSidebar) {
      setSideMenuExpanded(true);
    }
  }, [setSideMenuExpanded, currentTheorie]);

  const fetchTheorie = useCallback(
    async (id: number) => {
      setIsLoadingTheory(true);
      // Demo: serve theorie from client-side mock data
      if (id < 0) {
        const demoTheorie = DEMO_THEORIEEN[id];
        if (demoTheorie) {
          setCurrentTheorie(demoTheorie);
          setIsLoadingTheory(false);
          return;
        }
      }
      if (!currentStappenplan) {
        setIsLoadingTheory(false);
        setShowTheory(false);
        setSideMenuExpanded(true);
        return;
      }
      const result: TheorieResponse = await api.getTheorie(
        id,
        currentStappenplan.id
      );
      setCurrentTheorie(result);
      setIsLoadingTheory(false);
    },
    [api, currentStappenplan, setSideMenuExpanded]
  );

  const handleTheorieClick = useCallback(
    async (id: number) => {
      if (id > 0 && !isFeatureDiscoveryTourSuppressed()) {
        void markDiscovered("theorie", {
          source: "theorie_button_clicked",
          metadata: {
            theorie_id: id,
            stappenplan_id: currentStappenplan?.id ?? null,
            surface: "theory_panel"
          }
        });
      }
      setShowTheory(true);
      setIsLoadingTheory(true);
      setSideMenuExpanded(false);
      // Always reset theory width to default: 600px, capped at half the screen
      setTheoryMenuWidth(Math.min(600, Math.floor(window.innerWidth / 2)));

      await fetchTheorie(id);
    },
    [
      currentStappenplan?.id,
      fetchTheorie,
      markDiscovered,
      setSideMenuExpanded
    ]
  );

  const handleInlineTheorieClick = useCallback(
    async (id: number) => {
      if (inlineTheorie?.id === id) {
        setInlineTheorie(undefined);
        return;
      }
      if (id > 0 && !isFeatureDiscoveryTourSuppressed()) {
        void markDiscovered("theorie", {
          source: "theorie_button_clicked",
          metadata: {
            theorie_id: id,
            stappenplan_id: currentStappenplan?.id ?? null,
            surface: "inline_theory"
          }
        });
      }
      setIsLoadingInlineTheorie(true);
      if (id < 0) {
        const demo = DEMO_THEORIEEN[id];
        if (demo) {
          setInlineTheorie(demo);
          setIsLoadingInlineTheorie(false);
          return;
        }
      }
      if (!currentStappenplan) {
        setIsLoadingInlineTheorie(false);
        return;
      }
      const result: TheorieResponse = await api.getTheorie(
        id,
        currentStappenplan.id
      );
      setInlineTheorie(result);
      setIsLoadingInlineTheorie(false);
    },
    [
      api,
      currentStappenplan,
      inlineTheorie?.id,
      markDiscovered
    ]
  );

  const closeInlineTheorie = useCallback(() => {
    setInlineTheorie(undefined);
    setIsLoadingInlineTheorie(false);
  }, []);

  const providerValue = useMemo(
    () => ({
      showTheory,
      setShowTheory,
      isLoadingTheory,
      setIsLoadingTheory,
      theoryMenuWidth,
      setTheoryMenuWidth,
      currentTheorie,
      closeTheory,
      handleTheorieClick,
      inlineTheorie,
      isLoadingInlineTheorie,
      handleInlineTheorieClick,
      closeInlineTheorie
    }),
    [
      showTheory,
      setShowTheory,
      isLoadingTheory,
      setIsLoadingTheory,
      theoryMenuWidth,
      setTheoryMenuWidth,
      currentTheorie,
      closeTheory,
      handleTheorieClick,
      inlineTheorie,
      isLoadingInlineTheorie,
      handleInlineTheorieClick,
      closeInlineTheorie
    ]
  );

  return (
    <TheoryContext.Provider value={providerValue}>
      {children}
    </TheoryContext.Provider>
  );
}

export function useTheoryContext() {
  const context = useContext(TheoryContext);
  if (context === undefined) {
    throw new Error(
      "useTheoryContext must be used within a TheoryContextProvider"
    );
  }
  return context;
}

// Safe version that returns null if not in context
export function useTheoryContextSafe() {
  const context = useContext(TheoryContext);
  return context;
}
